<template>
    <section class="results-panel">
        <div class="results-panel-content" tabindex="-1">
            <header class="results-panel-header">
                <div class="left">
                    <template v-if="loadingDocuments">
                        Loading Documents...
                    </template>
                    <template v-else>
                        <div>{{ numberWithCommas(totalDocuments) }} Documents.</div>
                        <button class="button-gray" v-if="canReset" @click="resetFiltersAndSorter">Reset</button>
                    </template>
                </div>
                <div class="right">
                  <button class="pure button-with-icon" :class="{disabled: loadingDocuments}"
                          @click="!loadingDocuments && refreshResults()">
                    <i class="gf gf-refresh"></i> Reload
                  </button>
                  <button v-if="isUserManager" class="pure button-with-icon" @click="goExport"
                          :class="{disabled: exportManager.on || exportManager.working}"><i
                      class="gf gf-linkout"></i> Export
                  </button>
                  <simple-dropdown :click-dropdown-menu-to-hide="false" :alignment="1" :dropdown-arrow="true"
                                   @dropdownMenuShown="onColumnEditorDropdownShown"
                  >
                    <template v-slot:triggerButtonContent>
                      <button class="pure button-with-icon"><i class="gf gf-edit-2"></i> Edit Columns</button>
                    </template>
                        <template v-slot:dropdownMenu>
                            <div class="dropdown-content">
                                <properties-visible-editor ref="propertiesVisibleEditor"
                                                           :attribute-properties="attributeProperties"
                                                           @updateHiddenMaps="updateHiddenMaps"
                                                           @resetHiddenMaps="resetHiddenMaps"
                                                           @fillHiddenMaps="fillHiddenMaps"
                                ></properties-visible-editor>
                            </div>
                        </template>
                    </simple-dropdown>
                    <simple-dropdown class="more-dropdown-button ml-3" :alignment="1">
                        <template v-slot:dropdownMenu>
                            <ul>
                                <li>
                                    <button :disabled="addDocumentsManager.working" @click="showDocumentsAdder">Add
                                        Documents
                                    </button>
                                </li>
                            </ul>
                        </template>
                    </simple-dropdown>
                </div>
            </header>
            <div class="results-table-container-wrapper">
                <results-table-container :rows="rows" :project-id="projectId"
                                         :row-counter-base="rowCounterBase"
                                         :attribute-property-hidden-maps="attributePropertyHiddenMaps"
                                         :records-selection-mode-on="exportManager.on"
                                         :selected-global-document-ids="selectedGlobalDocumentIds"
                                         @addIntoSelectedGlobalDocumentIds="addIntoSelectedGlobalDocumentIds"
                                         @removeFromSelectedGlobalDocumentIds="removeFromSelectedGlobalDocumentIds"
                                         @clearSelectedGlobalDocumentIds="clearSelectedGlobalDocumentIds"
                                         @updateCanReset="updateCanReset"
                                         ref="resultsTableContainer"></results-table-container>
                <div class="message-layer" v-if="!showingDocuments">
                    <template v-if="showingEmptyIndicator">
                        <div class="global-message-box">
                            <h1>No Results Found</h1>
                        </div>
                    </template>
                    <template v-else-if="errorLoadingDocuments">
                        <div class="global-message-box">
                            <div class="text-center">
                                <h1>There was something not right while loading documents!</h1>
                                <button @click="refreshResults" style="margin-top: 0.25rem">Reload</button>
                            </div>
                        </div>
                    </template>
                    <template v-else-if="loadingDocuments">
                        <div class="global-message-box">
                            <spinner></spinner>
                        </div>
                    </template>
                </div>
            </div>
            <footer class="results-panel-footer">
                <select class="page-size-select mr-2" title="Select Page Size"
                        @change="$emit('changePageSize', $event.target.value)">
                    <option disabled>Select Page Size</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                </select>
                <div class="pagination">
                    <button @click="clickFirstPagePagination" :class="{disabled: !isFirstPageButtonEnabled()}"><i
                            class="gf gf-firstpage"></i></button>
                    <button @click="goToPage(currentPage - 1)" :class="{disabled: !isPrevPageButtonEnabled()}">
                        <i
                                class="gf gf-prevous"></i></button>
                    <label class="center"><input :value="displayedPageNumber" ref="currentPageDisplayer"
                                                 @keydown.esc="onCurrentPageDisplayEscape"
                                                 @blur="onCurrentPageDisplayBlur"
                                                 @keydown.enter="onCurrentPageDisplayEnter"><span> of </span><span>{{
                            totalPages
                        }}</span></label>
                    <button @click="goToPage(currentPage + 1)" :class="{disabled: !isNextPageButtonEnabled()}">
                        <i
                                class="gf gf-next"></i></button>
                    <button @click="clickLastPagePagination" :class="{disabled: !isLastPageButtonEnabled()}"><i
                            class="gf gf-lastpage"></i></button>
                </div>
                <div class="legends ml-auto">
                    <div><i class="bg-legend-filled-confirmed"></i> <span>Filled, Confirmed</span></div>
                    <div><i class="bg-legend-auto-filled-unconfirmed"></i> <span>Auto-Filled, Unconfirmed</span></div>
                    <div><i class="bg-legend-unfilled-with-candidates"></i> <span>Unfilled with Candidates</span></div>
                    <div><i class="bg-legend-unfilled-without-candidates"></i> <span>Unfilled without Candidates</span>
                    </div>
                </div>
            </footer>
        </div>
        <div class="export-outlook" v-if="exportManager.on">
            <select-dropdown-button class="mr-3"
                                    @selectAll="selectAll"
                                    @selectPage="selectPage"
                                    @clearAll="clearAll"></select-dropdown-button>
            <template v-if="selectedGlobalDocumentIds.length === 0">
                <span>To continue, select documents to export.</span>
            </template>
            <template v-else>
                <span>{{ selectedGlobalDocumentIds.length }} documents selected.</span>
            </template>
            <button v-if="selectedGlobalDocumentIds.length" class="button-primary ml-3 mr-3"
                    :class="{disabled: selectedGlobalDocumentIds.length===0}"
                    @click="downloadExport">Export CSV
            </button>
            <label class="checkbox-label" v-if="selectedGlobalDocumentIds.length"><input type="checkbox"
                                                                                         ref="doneDocsOnlyCheckbox">
                <span>Done documents only</span></label>
            <button @click="exitExportMode" class="ml-auto">Cancel</button>
        </div>
        <modal class="add-documents-container" v-if="addDocumentsManager.on" @close="hideDocumentsAdder">
            <template v-slot:header>
                <h3>Add Documents to {{ projectIdToName[projectId] }}</h3>
            </template>
            <template v-slot:body>
                <div class="body">
                    <div class="mb-1">Enter File ID:</div>
                    <div class="mb-2"><input ref="fileIdInput" placeholder="e.g. 2020121400648" class="w-100"
                                             @keydown.enter="confirmDocumentsAdder"
                    ></div>
                    <div class="text-gray"><em>Hint: Multiple documents separated by "<strong>,</strong>"</em></div>
                </div>
            </template>
            <template v-slot:footer>
                <div class="d-flex justify-content-end">
                    <button class="mr-2" @click="hideDocumentsAdder">Cancel</button>
                    <button class="button-primary" @click="confirmDocumentsAdder">Add</button>
                </div>
            </template>
        </modal>
    </section>
</template>

<script>
import ResultsTableContainer from "@/components/ResultsTableContainer";
import {numberWithCommas, exportDataToCSV, renderValueInCandidateItem} from "@/utils";
import SimpleDropdown from "@/components/SimpleDropdown";
import PropertiesVisibleEditor from "@/components/PropertiesVisibleEditor";
import {exportSelectedData, searchDocuments, addDocumentsIntoProject} from "@/apis";
import {projectIdToTemplate, statusIds, projectIdToName} from "@/config";
import SelectDropdownButton from "@/components/SelectDropdownButton";
import Spinner from "@/components/Spinner";
import Modal from "@/components/Modal";

export default {
    name: "ResultsPanel",
    components: {
        Modal,
        Spinner, SelectDropdownButton, PropertiesVisibleEditor, SimpleDropdown, ResultsTableContainer
    },
    props: {
        totalDocuments: Number,
        rows: Array,
        totalPages: Number,
        currentPage: Number,
        projectId: String,
        rowCounterBase: Number,
        searchFormData: Object,
        loadingDocuments: Boolean,
        errorLoadingDocuments: Boolean,
        showingDocuments: Boolean,
        showingEmptyIndicator: Boolean,
    },
    methods: {
        resetFiltersAndSorter() {
            this.$refs.resultsTableContainer.resetFiltersAndSorter();
        },
        updateCanReset(b) {
            this.canReset = b;
        },
        selectOrUnselectAllRows(selectOrUnselect) {
            this.$refs.resultsTableContainer.selectOrUnselectAllRows(selectOrUnselect);
        },
        selectPage() {
            this.selectOrUnselectAllRows(true);
        },
        selectAll() {
          const {projectId} = this;
          const data = this.$refs.resultsTableContainer.getPips();
          const formData = {
            project_id: projectId,
            pips: data.pips,
            involvedJoinedEventObjects: data.involvedJoinedEventObjects
          };

          let toast = this.$toasted.show(`Preparing to select documents in all pages.`,);
          searchDocuments(JSON.stringify(formData),
              0,
              1000000,
              true).then((r) => {
            this.selectedGlobalDocumentIds = r.data;
            this.$nextTick(() => {
              this.$refs.resultsTableContainer.adjustRecordCheckboxes();
            })
                toast.text(`Selected ${r.data.length} Documents from all pages.`).goAway(3000);
            }).catch(() => {
                toast.text('Error trying to select all pages. Try again later.').goAway(5000);
            })
        },
        clearAll() {
            this.clearSelectedGlobalDocumentIds();
            this.$nextTick(() => {
                this.$refs.resultsTableContainer.adjustRecordCheckboxes();
            })
        },
        downloadExport() {
            const {selectedGlobalDocumentIds, enableSerial} = this;
            if (!selectedGlobalDocumentIds.length) {
                return;
            }

            this.exportManager.working = true;

            let toast = this.$toasted.show(`Preparing to export ${selectedGlobalDocumentIds.length} documents.`,);

            const shownProperties = [].concat(this.$refs.resultsTableContainer.shownProperties);
            const ids = [].concat(selectedGlobalDocumentIds);
            const doneOnly = this.$refs.doneDocsOnlyCheckbox.checked;

            this.exitExportMode();

            exportSelectedData(ids).then((r) => {
                let rows = [];
                let row;
                let values;
                let value;
                let elem;
                row = [];
                shownProperties.forEach(property => {
                    if (property.skipWhileExport) {
                        return;
                    }
                    if (property.isAttribute) {
                        row.push(property.fullLabel);
                    } else {
                        row.push(property.label);
                    }
                })
                rows.push(row);

                let countEvents = 0;
                let countSerials = 0;
                let countDocuments = 0;

                r.data.forEach(documentObject => {
                    if (doneOnly && documentObject['status'] !== statusIds.done) {
                        return;
                    }
                    countDocuments++;
                    let addedRow = false;


                    const handleEvent = (eventObject, noCount) => {
                        if (!noCount) {
                            countEvents++;
                        }
                        row = []
                        values = eventObject.values || {};

                        shownProperties.forEach(property => {
                            if (property.skipWhileExport) {
                                return;
                            }
                            if (property.exportValue) {
                                value = property.exportValue(documentObject);
                                row.push(value);
                                return;
                            }
                            if (property.label === 'Serial ID') {
                                row.push(eventObject.serial_id)
                                return;
                            }
                            if (property.label === 'Event ID') {
                                row.push(eventObject.event_id)
                                return;
                            }
                            if (property.isAttribute) {
                                value = values[property.fieldId];
                                value = renderValueInCandidateItem(value, property.field);
                                row.push(value);
                            } else {
                                value = property.renderer(documentObject);
                                // render basic properties
                                if (property.renderHTML) {
                                    elem = document.createElement('div');
                                    elem.innerHTML = value;
                                    value = elem.innerText.trim();
                                }
                                row.push(value);
                            }
                        });

                        rows.push(row);

                        if (!addedRow) {
                            addedRow = true;
                        }
                    }

                    if (documentObject.serials) {
                        documentObject.serials.forEach(serialObject => {
                            countSerials++;
                            serialObject.events.forEach(handleEvent);
                        })
                    } else if (documentObject.events) {
                        documentObject.events.forEach(handleEvent);
                    }

                    if (!addedRow) {
                        handleEvent({
                            serial_id: '',
                            event_id: '',
                            value: {},
                        }, true);
                    }
                })

                toast.text('Your download should begin immediately.').goAway(3000)

                exportDataToCSV(rows, `DA Exported Documents - ${countEvents} Events, ${enableSerial ? (countSerials + ' Serials, ') : ''}${countDocuments} Documents.csv`);
            }).catch((e) => {
                // eslint-disable-next-line no-console
                console.error(e)
                toast.text('Error trying to export data. Try again later.').goAway(5000)
            }).always(() => {
                this.exportManager.working = false;
            })
        },
        addIntoSelectedGlobalDocumentIds(global_doc_id) {
            this.selectedGlobalDocumentIds.push(global_doc_id);
        },
        removeFromSelectedGlobalDocumentIds(global_doc_id) {
            this.selectedGlobalDocumentIds.splice(this.selectedGlobalDocumentIds.indexOf(global_doc_id), 1);
        },
        clearSelectedGlobalDocumentIds() {
            this.selectedGlobalDocumentIds = [];
        },
        goExport() {
            if (this.exportManager.on || this.exportManager.working) {
                return;
            }

            this.enterExportMode();
        },
        enterExportMode() {
            this.exportManager = {
                on: true,
                working: false,
            };
            this.clearSelectedGlobalDocumentIds();
        },
        exitExportMode() {
            this.exportManager.on = false;
        },
        updateHiddenMaps(payload) {
            this.$set(this.attributePropertyHiddenMaps, payload.propertyKey, payload.hidden);
        },
        resetHiddenMaps() {
            this.attributePropertyHiddenMaps = {};
        },
        fillHiddenMaps() {
            const maps = {};
            this.attributeProperties.forEach(v => {
                maps[v.propertyKey] = true;
            });
            this.attributePropertyHiddenMaps = maps;
        },
        onColumnEditorDropdownShown() {
            setTimeout(() => {
                this.$refs.propertiesVisibleEditor.fillValue(this.attributePropertyHiddenMaps);
            }, 0)
        },
        numberWithCommas: numberWithCommas,
        goToPage(page) {
            if (!(this.isPageValid(page))) {
                return;
            }
            this.$emit('goToPage', page)
        },
        isFirstPageButtonEnabled() {
            return this.currentPage > 1
        },
        isPrevPageButtonEnabled() {
            return this.currentPage > 0
        },
        isNextPageButtonEnabled() {
            return this.currentPage < this.totalPages - 1
        },
        isLastPageButtonEnabled() {
            return this.currentPage < this.totalPages - 2
        },
        setCurrentPageDisplay(value) {
            this.$refs.currentPageDisplayer.value = value;
        },
        onCurrentPageDisplayEscape() {
            this.$refs.currentPageDisplayer.blur();
        },
        onCurrentPageDisplayBlur() {
            let value = parseInt(this.$refs.currentPageDisplayer.value) - 1;
            if (isNaN(value)) {
                this.setCurrentPageDisplay(this.displayedPageNumber);
                return
            }
            if (this.isPageValid(value)) {
                this.goToPage(value)
            } else {
                this.setCurrentPageDisplay(this.displayedPageNumber);
            }
        },
        onCurrentPageDisplayEnter() {
            this.$refs.currentPageDisplayer.blur();
        },
        isPageValid(page) {
            return page >= 0 && page < this.totalPages;
        },
        focus() {
            this.$el.firstElementChild.focus();
        },
        scrollResultsTableContainerToInitial() {
            if (this.rows.length) {
                this.$refs.resultsTableContainer.scrollTo(0, 0);
            }
        },
        refreshResults() {
            this.$emit('refreshResults');
        },
        clickFirstPagePagination() {
            if (this.isFirstPageButtonEnabled()) {
                this.goToPage(0);
            }
        },
        clickLastPagePagination() {
            if (this.isLastPageButtonEnabled()) {
                this.goToPage(this.totalPages - 1);
            }
        },
        showDocumentsAdder() {
            if (this.addDocumentsManager.working) {
                return
            }
            this.addDocumentsManager.on = true;
            this.$nextTick(() => {
                this.$refs.fileIdInput.focus();
            })
        },
        hideDocumentsAdder() {
            this.addDocumentsManager.on = false;
        },
        confirmDocumentsAdder() {
            const fileInput = this.$refs.fileIdInput;
            const fileIds = fileInput.value.trim();
            if (!fileIds) {
                fileInput.focus();
                return
            }
            this.addDocumentsManager.working = true;
            this.addDocumentsManager.on = false;
            const toast = this.$toasted.show('Adding documents...');
            addDocumentsIntoProject(this.projectId, fileIds).then(({added}) => {
                toast.text(`Done! ${added} document${added > 1 ? 's' : ''} added.`);
            }).catch(() => {
                toast.text('There was something not right while adding documents. Try again later.')
            }).always(() => {
                this.addDocumentsManager.working = false;
                this.$emit('refreshSilently');
                toast.goAway(5000);
            })
        },
    },
    computed: {
        displayedPageNumber() {
            return this.currentPage + 1;
        },
        isUserManager() {
            return this.$store.state.isUserManager;
        },
        projectTemplate() {
            return projectIdToTemplate[this.projectId];
        },
        enableSerial() {
            return this.projectTemplate && this.projectTemplate['enable_serial'];
        },
    },
    data() {
        return {
            attributePropertyHiddenMaps: {},
            attributeProperties: [],
            exportManager: {
                on: false,
                working: false,
            },
            selectedGlobalDocumentIds: [],
            canReset: false,
            projectIdToName,
            addDocumentsManager: {
                on: false,
                working: false,
            }
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.attributeProperties = this.$refs.resultsTableContainer.attributeProperties;
        })
    }
}
</script>

<style scoped lang="scss">
@import "../assets/variables";

.results-panel {
    .results-panel-content {
        height: 100%;

        &:focus {
            outline: none;
        }

        .results-panel-header {
            height: 2.5rem;
            display: flex;
            align-items: center;
            padding: 0 1rem;

            .export-button {
                margin-left: auto;
            }

            .search-conditions {
                color: #777;

                &:before {
                    content: '(';
                }

                &:after {
                    content: ')';
                }
            }

            .right {
                margin-left: auto;
                display: flex;
                align-items: center;

                button {
                    &:not(:last-child) {
                        margin-right: 1rem;
                    }
                }
            }

            .left {
                display: flex;
                align-items: center;

                button {
                    font-size: 0.86em;
                    margin: 0 0.5rem;
                }
            }
        }

        .results-table-container-wrapper {
            height: calc(100% - 4.5rem);
            position: relative;

            .message-layer {
                position: absolute;
                top: 49px;
                right: 0;
                bottom: 0;
                left: 0;
                background-color: white;
            }
        }

        .results-panel-footer {
            display: flex;
            align-items: center;
            height: 2rem;
            padding: 0 0.5rem;
            border-top: 1px solid lightgray;
            font-size: 14px;

            .pagination {
                display: flex;
                align-items: center;

                button {
                    margin: 0 0.2rem;
                    font-size: 0.86em;
                    background-color: transparent;
                    border: none;
                    padding: 1px 5px;
                }

                input {
                    width: 2.5rem;
                    text-align: center;
                }

                .center {
                    margin: 0 0.5rem;
                    display: flex;
                    align-items: center;

                    & > * {
                        margin: 0 0.1rem;
                    }
                }
            }

            .page-size-select {
                max-width: 44px;
            }
        }
    }
}

.export-outlook {
    position: fixed;
    top: 0;
    right: 0;
    height: 54px;
    left: 0;
    background: white linear-gradient(to top, white, #fafafa);
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 1px 12px -4px rgba(0, 0, 0, 0.25);
    display: flex;
    align-items: center;
    padding: 0 1rem;
    z-index: 111;
    transition: transform 0.2s ease;
}

.modal {
    .header {
        font-size: 1rem;
    }

    .footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;

        button {
            margin-left: 0.5rem;
            min-width: 50px;
        }
    }

    input {
        width: 100%;
    }
}

.add-documents-container {
    .body {
        width: 320px;
    }

    button {
        min-width: 68px;
    }
}
</style>
<style lang="scss">
.slide-down-enter {
    transform: translateY(-54px);
}

.slide-down-leave-active {
    transform: translateY(-54px);
}

.modal-enter {
    opacity: 0;
}

.modal-leave-active {
    opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
    -webkit-transform: scale(1.1);
    transform: scale(1.1);
}
</style>
